/**
 * Bridges the shared single-source content (SKU display strings + feature tips)
 * into the Mini App's i18next resource bundles at init time.
 *
 * The `common.sku.*` and `flows.<tool>.tips` keys used to be duplicated in the
 * locale JSON; they now live in @wds/astro-bot-shared and are injected here, so
 * components keep calling `t('sku.<code>.name')` / `t('check.tips.body')`
 * unchanged. Edit the copy in shared, not in three JSON files.
 */

import { SKU_I18N } from '@wds/astro-bot-shared/catalog';
import { FEATURE_TIPS } from '@wds/astro-bot-shared/content/feature-tips';

type Loc = 'en' | 'ru' | 'uk';

interface SkuStrings {
  name: string;
  desc: string;
  bullets?: string[];
}

/** Reconstruct the `common.sku` block for a locale from the shared catalog. */
export function skuBlockForLocale(loc: Loc): Record<string, SkuStrings> {
  const out: Record<string, SkuStrings> = {};
  for (const [code, v] of Object.entries(SKU_I18N)) {
    const entry: SkuStrings = {
      name: v.name[loc] ?? v.name.en,
      desc: v.desc[loc] ?? v.desc.en,
    };
    if (v.bullets) entry.bullets = v.bullets[loc] ?? v.bullets.en;
    out[code] = entry;
  }
  return out;
}

/**
 * Build the `{ <tool>: { tips: { title, body } } }` fragment for a locale, to be
 * deep-merged into the `flows` namespace (the tool objects keep their other
 * keys). The shared `check` tool maps to the Mini App's `check` key.
 */
export function tipsFragmentForLocale(loc: Loc): Record<string, { tips: { title: string; body: string } }> {
  const out: Record<string, { tips: { title: string; body: string } }> = {};
  for (const [tool, v] of Object.entries(FEATURE_TIPS)) {
    out[tool] = { tips: { title: v.title[loc] ?? v.title.en, body: v.body[loc] ?? v.body.en } };
  }
  return out;
}
